/**
 * React Query lookup-cache helpers.
 *
 * Lookups (weighing console, booking form, LookupModal) use keys like:
 *   ['lookup', <segment>, ...]
 *
 * SignalR entity names are PascalCase plurals ("Accounts"). Segments vary
 * (accounts, accountId). Always remove every alias so no stale options remain.
 */

/** @type {Record<string, string[]>} */
export const LOOKUP_SEGMENTS_BY_ENTITY = {
  Accounts: ['accounts', 'accountId', 'account'],
  accounts: ['accounts', 'accountId', 'account'],
  Commodities: ['commodities', 'commodityId', 'commodity'],
  commodities: ['commodities', 'commodityId', 'commodity'],
  Contracts: ['contracts', 'contractId', 'contract'],
  contracts: ['contracts', 'contractId', 'contract'],
  Drivers: ['drivers', 'driverId', 'driver'],
  drivers: ['drivers', 'driverId', 'driver'],
  Hauliers: ['hauliers', 'haulierId', 'haulier'],
  hauliers: ['hauliers', 'haulierId', 'haulier'],
  Vehicles: ['vehicles', 'vehicleId', 'vehicle'],
  vehicles: ['vehicles', 'vehicleId', 'vehicle'],
  Trailers: ['trailers', 'trailerId', 'trailer'],
  trailers: ['trailers', 'trailerId', 'trailer'],
  Locations: ['locations', 'locationId', 'fromLocationId', 'toLocationId'],
  locations: ['locations', 'locationId', 'fromLocationId', 'toLocationId'],
  Ships: ['ships', 'shipId', 'ship'],
  ships: ['ships', 'shipId', 'ship'],
  LaAreas: ['laAreas', 'laAreaId'],
  laAreas: ['laAreas', 'laAreaId'],
  laareas: ['laAreas', 'laAreaId'],
  Sites: ['sites', 'siteId'],
  sites: ['sites', 'siteId'],
  TransTypes: ['transTypes', 'transTypeId'],
  transTypes: ['transTypes', 'transTypeId'],
  transtypes: ['transTypes', 'transTypeId'],
  Users: ['users', 'userId'],
  users: ['users', 'userId'],
};

/**
 * Resolve lookup key segments for a SignalR / list entity name.
 * @param {string} entityName
 * @returns {string[]}
 */
export function getLookupSegmentsForEntity(entityName) {
  if (!entityName) return [];
  const mapped = LOOKUP_SEGMENTS_BY_ENTITY[entityName]
    ?? LOOKUP_SEGMENTS_BY_ENTITY[String(entityName).toLowerCase()];
  if (mapped) return mapped;
  const lower = String(entityName).toLowerCase();
  return [lower];
}

/**
 * Drop cached contract-related payloads used by weighing-console autofill.
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {number|string|null} [contractId] When omitted, all contract-related entries are removed.
 */
export function removeContractRelatedQueries(queryClient, contractId = null) {
  if (!queryClient) return;
  if (contractId != null && contractId !== '') {
    queryClient.removeQueries({ queryKey: ['contract', 'related', contractId], exact: true });
    queryClient.removeQueries({ queryKey: ['contract', 'related', String(contractId)], exact: true });
    return;
  }
  queryClient.removeQueries({ queryKey: ['contract', 'related'], exact: false });
}

/**
 * Drop all cached lookup queries for an entity (active observers refetch).
 * Prefer remove over invalidate so inactive caches cannot serve stale options.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string} entityName SignalR or list entity name (e.g. 'Accounts' / 'accounts')
 */
export function removeEntityLookupQueries(queryClient, entityName) {
  if (!queryClient || !entityName) return;
  const segments = getLookupSegmentsForEntity(entityName);
  for (const segment of segments) {
    queryClient.removeQueries({ queryKey: ['lookup', segment], exact: false });
  }
  // Legacy booking-form prefix (pre-lookup-key migration)
  const legacy = String(entityName).toLowerCase();
  queryClient.removeQueries({ queryKey: ['booking', legacy], exact: false });
  if (legacy === 'laareas') {
    queryClient.removeQueries({ queryKey: ['booking', 'laAreas'], exact: false });
  }
  if (legacy === 'contracts' || segments.includes('contracts') || segments.includes('contractId')) {
    removeContractRelatedQueries(queryClient);
  }
}
